import React, { useEffect, useRef, useState } from "react";
import {
    IonRadioGroup,
    IonRadio, IonItem, IonButton,
    IonLabel,
    IonModal,
    IonContent
} from "@ionic/react";
import { Helmet } from "react-helmet";



interface QuestionData {
    question: string;
    options: OptionData[];
}

interface OptionData {
    text: string;
    isCorrect: boolean;
}


const SentenceFormation: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedAnswer(option);
        const correctOption = questions[currentIndex].options.find(opt => opt.isCorrect);

        if (selectedAnswer === correctOption?.text) {
            setShowImage(true);
            setTimeout(() => {
                if (currentIndex != questions.length - 1) {
                    setCurrentIndex(currentIndex + 1);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
                setShowImage(false);
                setSelectedAnswer(null);
            }, 5000); // Delay reduced to 3 seconds
        } else {
            setMessage("❌ Incorrect, try again!");
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'SentenceFormation.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/SentenceFormation.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Sentence Formation Worksheets for UKG | Simple Sentence Building Practice"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Help UKG students build strong grammar skills with engaging sentence formation activities at Mama’s Guidance, designed to improve vocabulary, structure, and communication.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Sentence Formation</h1>

                                <p>
                                    Sentence formation is a key step in helping children communicate clearly in English. At the UKG level, children begin to learn how to frame <strong>simple and meaningful sentences</strong>. These worksheets provide a structured yet fun way to practice sentence building using common sight words, pictures, and prompts.
                                </p>

                                <h2>🧠 What Is Sentence Formation?</h2>
                                <p>
                                    Sentence formation is the process of arranging words in the correct order to express a complete thought. For example: <b><em>“The dog runs.”</em></b>
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Build simple sentences using 3 to 5 words</li>
                                    <li>Understand <strong>subject + verb + object</strong> structure</li>
                                    <li>Use correct grammar and punctuation</li>
                                    <li>Improve vocabulary and communication skills</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Rearrange jumbled words to make sentences</li>
                                    <li>Write sentences using picture prompts</li>
                                    <li>Choose the correct sentence from options</li>
                                    <li>Match images with written sentences</li>
                                    <li>Fill in the blanks with suitable words</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Speak in simple sentences and encourage children to repeat</li>
                                    <li>Play <strong>“build a sentence”</strong> games using flashcards</li>
                                    <li>Use picture books to prompt sentence discussions</li>
                                    <li>Correct gently and praise their sentence attempts</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Let your child describe their toys or activities in full sentences: <strong>“This is my car. It is red.”</strong> Repeat daily for quick improvement!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Sentence Formation</h1>

                            <p>
                                Sentence formation is a key step in helping children communicate clearly in English. At the UKG level, children begin to learn how to frame <strong>simple and meaningful sentences</strong>. These worksheets provide a structured yet fun way to practice sentence building using common sight words, pictures, and prompts.
                            </p>

                            <h2>🧠 What Is Sentence Formation?</h2>
                            <p>
                                Sentence formation is the process of arranging words in the correct order to express a complete thought. For example: <b><em>“The dog runs.”</em></b>
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Build simple sentences using 3 to 5 words</li>
                                <li>Understand <strong>subject + verb + object</strong> structure</li>
                                <li>Use correct grammar and punctuation</li>
                                <li>Improve vocabulary and communication skills</li>
                            </ul>

                            <h2>🖍️ Activities in the Worksheets</h2>
                            <ul>
                                <li>Rearrange jumbled words to make sentences</li>
                                <li>Write sentences using picture prompts</li>
                                <li>Choose the correct sentence from options</li>
                                <li>Match images with written sentences</li>
                                <li>Fill in the blanks with suitable words</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Speak in simple sentences and encourage children to repeat</li>
                                <li>Play <strong>“build a sentence”</strong> games using flashcards</li>
                                <li>Use picture books to prompt sentence discussions</li>
                                <li>Correct gently and praise their sentence attempts</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Let your child describe their toys or activities in full sentences: <strong>“This is my car. It is red.”</strong> Repeat daily for quick improvement!
                            </p>
                        </article>
                        <h2>{questions[currentIndex]?.question}</h2>

                        <IonRadioGroup
                            value={selectedAnswer}
                            onIonChange={(e) => handleSubmit(e.detail.value)}
                        >
                            {questions[currentIndex]?.options.map((option, index) => (
                                <IonItem
                                    className={` ${selectedAnswer === option.text
                                        ? option.isCorrect === true
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}
                                    key={index}>
                                    <IonLabel>{option.text}</IonLabel>
                                    <IonRadio slot="start" value={option.text} />
                                </IonItem>
                            ))}
                        </IonRadioGroup>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default SentenceFormation;
